export const site = {
  brand: "votanphuoc.space",
  legalEntity: "votanphuoc.space",
  email: "tht2008@votanphuoc.space",
  url: "https://votanphuoc.space",
  tagline: "AI agents, agentic workflows & apps",

  nav: [
    { label: "Home", href: "#home", testId: "nav-link-home" },
    { label: "About", href: "#about", testId: "nav-link-about" },
    { label: "Solutions", href: "#solutions", testId: "nav-link-solutions" },
    { label: "Our Mission", href: "#mission", testId: "nav-link-mission" },
    { label: "Open Source", href: "#open-source", testId: "nav-link-opensource" },
    { label: "Contact", href: "#contact", testId: "nav-link-contact" },
  ],

  hero: {
    eyebrow: "AI Agents · Agentic Workflows · Apps",
    lines: ["AI agents that do", "the busywork.", "You keep control."],
    sub: "votanphuoc.space builds autonomous AI agents and agentic apps that turn complex, multi-step digital work into supervised, reliable pipelines — made for people who use AI every day.",
    primaryCta: { label: "Explore Solutions", href: "#solutions" },
    secondaryCta: { label: "Our Mission", href: "#mission" },
    image:
      "https://images.unsplash.com/photo-1655993810480-c15dccf9b3a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFpJTIwdGVjaCUyMG5ldHdvcmslMjBnZW9tZXRyeSUyMG1pbmltYWxpc218ZW58MHx8fGJsdWV8MTc5MTQ0OTE5OHww&ixlib=rb-4.1.0&q=85",
    imageAlt: "Abstract wireframe network of connected nodes representing an agentic AI system",
    chips: ["perception", "planning", "execution", "memory"],
  },

  marquee: [
    "Agentic Workflows",
    "Autonomous Planning",
    "Contextual Memory",
    "Multi-Tool Integration",
    "Human-Centered AI",
    "Reliable Execution",
  ],

  about: {
    eyebrow: "About",
    heading: "A studio for applied agentic AI",
    story: [
      "votanphuoc.space works at the intersection of AI research and everyday utility. We design and build AI agents — software that perceives context, plans multi-step work, uses tools, and reports back to a human who stays in charge.",
      "Our focus is practical: reducing the cognitive load and repetitive digital toil that frequent AI users know well. Instead of another chat window, we build systems that carry context across tools and finish the job.",
      "Transparency starts with our name: votanphuoc.space is both our brand and our legal entity. One name, one accountable team — no shell structures, no ambiguity about who you are dealing with.",
    ],
    pillars: [
      {
        title: "Agent architecture",
        text: "Planning, memory and tool-use designed as first-class systems, not prompt hacks.",
      },
      {
        title: "Workflow orchestration",
        text: "Multi-step pipelines with checkpoints, retries and full execution logs.",
      },
      {
        title: "Less digital toil",
        text: "We measure success in hours returned to people, not in model benchmarks.",
      },
      {
        title: "Transparent operations",
        text: "Clear identity, clear roadmap, clear separation of shipping vs. planned work.",
      },
    ],
  },

  solutions: {
    eyebrow: "Solutions",
    heading: "Practical agentic systems, not demos",
    sub: "Everything we build answers a real workflow problem. Here is what we make, who it is for, and what it gives back.",
    items: [
      {
        id: "agentic-workflows",
        testId: "solution-card-workflows",
        icon: "workflow",
        title: "Autonomous Agentic Workflows",
        problem:
          "Frequent AI users lose hours copying context between tools and re-prompting models for every step of a task.",
        solution:
          "Self-orchestrating agent pipelines that decompose a complex objective into ordered steps, call the right tools, and recover from errors automatically.",
        benefit: "Reclaim hours every week with zero context-switching.",
      },
      {
        id: "custom-ai-apps",
        testId: "solution-card-apps",
        icon: "app",
        title: "Tailored AI Agent Apps",
        problem:
          "Generic chat interfaces lack domain-specific tools, persistent memory, and predictable structured output.",
        solution:
          "Custom agentic applications engineered with purpose-built toolsets, schema-validated outputs, and memory banks tuned to your domain.",
        benefit: "High-precision results matched to your exact operational needs.",
      },
      {
        id: "human-in-the-loop",
        testId: "solution-card-oversight",
        icon: "shield",
        title: "Human-Centered Oversight",
        problem:
          "Fully unmonitored agents risk unexpected side effects and a quiet loss of control over automated work.",
        solution:
          "Granular approval checkpoints and real-time execution logs that keep a human inside every decision loop that matters.",
        benefit: "Delegate to agents with confidence — you always have the final say.",
      },
    ],
  },

  mission: {
    eyebrow: "Our Mission",
    heading: "Technology for Humanity",
    intro:
      "We believe agentic AI should amplify human capability, not replace human judgment. That belief shapes who we build for, what our products improve, and how we hold ourselves accountable.",
    pillars: [
      {
        testId: "mission-pillar-who",
        icon: "users",
        title: "Who we help",
        text: "People who already rely on AI daily — developers, analysts, founders, creators — and still spend too much of that time on manual glue work between tools.",
      },
      {
        testId: "mission-pillar-what",
        icon: "spark",
        title: "What improves",
        text: "Fewer repetitive steps, fewer copy-paste errors, and auditable execution. Agents do the legwork; people keep intent, taste and final approval.",
      },
      {
        testId: "mission-pillar-accessibility",
        icon: "access",
        title: "Access & responsibility",
        text: "Interfaces approachable for non-experts, privacy-first data handling, and honest limits on what automation should decide on its own.",
      },
    ],
    now: {
      title: "What we do today",
      points: [
        "Building production agent workflows for frequent AI users",
        "Human approval checkpoints in every autonomous pipeline",
        "Execution logs designed to be read by people, not just machines",
      ],
    },
    next: {
      title: "Where we are heading",
      points: [
        "Accessibility-first agent interfaces for non-technical users",
        "Open benchmarks and orchestration tooling (see Open Source)",
        "Broader language support so agentic tools serve more communities",
      ],
    },
    note: "votanphuoc.space is a for-profit technology studio. Our mission guides what we build — it is not a claim of charitable or non-profit status.",
  },

  openSource: {
    eyebrow: "Open Source",
    badge: "Planned direction — no public repositories yet",
    heading: "Open by intent, honest about status",
    why: {
      title: "Why we care",
      text: "Autonomous systems earn trust when their behavior can be inspected. Open benchmarks, transparent frameworks and shared tooling let the whole community verify what agents actually do — not just what vendors claim.",
    },
    planned: {
      title: "What we plan to release",
      items: [
        "Core workflow orchestrator — the engine behind our agent pipelines",
        "Agent utility toolkit — memory, tool-use and schema-output helpers",
        "Evaluation harness — reproducible tests for agent reliability",
      ],
    },
    status:
      "These components are in active internal staging and will be published on this domain as they mature — with licenses, documentation and contribution guides attached. Until a repository link appears here, nothing should be assumed public.",
    ctaLabel: "Register your interest by email",
  },

  contact: {
    eyebrow: "Contact",
    heading: "Tell us about your workflow",
    sub: "Questions, collaborations, or a repetitive process you wish an agent would handle — write to us. Every message lands directly in our inbox and is read by a human.",
  },
};
